import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useState } from 'react';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const phoneAlt = (formData.get('phone_alt') as string || '').trim();
    if (phoneAlt) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9ng8k8bdkpk99j7gj60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries()).filter(([k]) => k !== 'phone_alt').map(([k, v]) => [k, v.toString()])
        ).toString(),
      });
      const responseText = await response.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const stats = [
    { value: '50万+', label: '日活跃用户' },
    { value: '10万+', label: '影视资源' },
    { value: '99.9%', label: '播放成功率' },
    { value: '8年', label: '稳定运营' },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-20 bg-background-50">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        <section className="w-full px-4 md:px-6 py-10">
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center mb-14">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 font-bold mb-4">
                关于80s电影网
              </h1>
              <p className="text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                始于对电影的热爱，忠于为用户提供最优质的观影体验。80s电影网致力于打造国内领先的免费高清影视平台。
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
              {stats.map((stat) => (
                <div key={stat.label} className="bg-background-100 rounded-xl p-6 text-center">
                  <p className="font-heading text-2xl md:text-3xl text-primary-500 font-bold mb-1">
                    {stat.value}
                  </p>
                  <p className="text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Brand Story */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
              <div>
                <h2 className="font-heading text-2xl text-foreground-950 font-bold mb-4">品牌故事</h2>
                <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    80s电影网创立于2018年，名字源自对80年代电影黄金时代的致敬。那个年代诞生了无数经典作品，也孕育了我们对电影艺术的热爱与追求。
                  </p>
                  <p>
                    我们深知影迷对于"高清画质"和"流畅播放"的渴望。因此从创立之初，我们就投入大量资源优化播放体验，自主研发极速播引擎，真正实现"点开即播不缓冲"的承诺。
                  </p>
                  <p>
                    经过多年的发展，80s电影网已成长为国内领先的免费影视平台，日活跃用户超过50万。我们始终坚持"免费、高清、不卡顿"的核心价值，为用户提供最优质的观影服务。
                  </p>
                </div>
              </div>
              <div>
                <h2 className="font-heading text-2xl text-foreground-950 font-bold mb-4">核心优势</h2>
                <div className="space-y-3">
                  {[
                    { icon: 'ri-4k-line', title: '蓝光画质', desc: '支持4K超清分辨率，带来影院级视觉体验' },
                    { icon: 'ri-flashlight-line', title: '极速播放', desc: '自研播放引擎，全网CDN加速，告别缓冲等待' },
                    { icon: 'ri-shield-check-line', title: '完全免费', desc: '无需注册、无需付费，所有资源免费观看' },
                    { icon: 'ri-device-line', title: '全设备支持', desc: '电脑、平板、手机无缝切换，随时随地观影' },
                    { icon: 'ri-refresh-line', title: '每日更新', desc: '热门影片第一时间上架，永不落伍' },
                    { icon: 'ri-user-heart-line', title: '用户至上', desc: '持续倾听用户反馈，不断优化产品体验' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3 bg-background-100 rounded-xl p-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                        <i className={`${item.icon} text-primary-500 text-lg`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">{item.title}</p>
                        <p className="text-xs text-foreground-500 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-2xl text-foreground-950 font-bold mb-4">联系我们</h2>
                <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                  <p>如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们。我们的团队会在24小时内回复。</p>
                  <div className="space-y-3 mt-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg">
                        <i className="ri-mail-line text-secondary-600 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">电子邮箱</p>
                        <p className="text-sm font-medium text-foreground-950">contact@80sdyw.com</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg">
                        <i className="ri-customer-service-line text-secondary-600 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">客服热线</p>
                        <p className="text-sm font-medium text-foreground-950">400-888-8888</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl text-foreground-950 font-bold mb-4">在线留言</h2>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form="true"
                  className="bg-background-100 rounded-xl p-6 space-y-4"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full h-10 px-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full h-10 px-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                  </div>
                  <div className="form-honeypot">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <button
                    type="submit"
                    className="w-full h-11 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    提交留言
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">留言已成功提交，我们会尽快回复！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于80s电影网',
            description: '80s电影网 - 高清在线看免费不卡影视平台，提供蓝光画质极速播放体验。',
            url: 'https://www.80sdyw.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '80s电影网',
              url: 'https://www.80sdyw.com/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.80sdyw.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
              },
            ],
          }),
        }}
      />
    </>
  );
}